// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { newsListViewStyles } from "@guestsupply/Overrides/Widgets/NewsList/NewsListView";
import { color, createLinkIconStyles, ellipsis, styledScrollbar, xScreen } from "@guestsupply/XDL/Utilities";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import { css } from "styled-components";

newsListViewStyles.titleText = {
    size: 20,
    css: css`
        margin-bottom: 0;
        line-height: 22px;
    `,
};

newsListViewStyles.newsWrapper = {
    css: css`
        width: 100%;
        margin-bottom: 20px;
        border: 1px solid ${color("common.border")};
        border-radius: 4px;
    `,
};

newsListViewStyles.pagination = {
    cssOverrides: {
        pagination: css`
            border-top: 1px solid ${color("common.border")};
            padding-top: 15px;
        `,
    },
};

newsListViewStyles.summaryWrapper = {
    css: css`
        margin: 8px 0 16px;
        ${ellipsis(4)}

        & > p {
            margin: 0;
            font-size: 14px;
        }
    `,
};

newsListViewStyles.contentWrapper = {
    css: css`
        display: flex;
        flex-wrap: nowrap;
        padding: 16px;
        ${xScreen("lte", "xs")} {
            flex-wrap: wrap;
        }
    `,
};

newsListViewStyles.newsListImageWrapper = {
    css: css`
        width: 301px;
        height: 191px;
        margin-right: 22px;
        ${xScreen("lte", "xs")} {
            width: 100%;
            margin-right: 0;
            margin-bottom: 12px;
        }
    `,
};

newsListViewStyles.newsListImage = {
    css: css`
        height: 100%;
        width: 100%;
        border-radius: 4px;
        img {
            max-width: 100%;
            max-height: 100%;
            height: auto;
        }
    `,
};

newsListViewStyles.contentSummaryWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        width: 100%;
    `,
};

newsListViewStyles.summaryPlaceholder = {
    css: css`
        margin-bottom: 20px;
    `,
};

newsListViewStyles.linkStyles = {
    color: "text",
    typographyProps: {
        size: 18,
        css: css`
            line-height: 22px;
            ${ellipsis(2)}
        `,
    },
};

newsListViewStyles.readMoreLink = {
    ...createLinkIconStyles(ChevronRight, {
        icon: {
            iconProps: {
                css: css`
                    margin-left: 4px;
                `,
            },
        },
        css: css`
            width: fit-content;
        `,
    }),
};

newsListViewStyles.newsListDetails = {
    css: css`
        height: 31px;
        display: flex;
        align-items: center;
        background-color: ${color("common.ui2")};
        border-radius: 0 0 4px 4px;
        padding: 8px 16px;
    `,
};

newsListViewStyles.authorBy = {
    size: 12,
    css: css`
        text-transform: capitalize;
        margin-right: 5px;
    `,
};

newsListViewStyles.authorText = {
    size: 12,
    css: css`
        font-weight: bold;
        margin-right: 8px;
    `,
};

newsListViewStyles.publishDateText = {
    size: 12,
    css: css`
        padding-right: 8px;
    `,
};

newsListViewStyles.listTitle = {
    variant: "h1",
};

newsListViewStyles.category = {
    size: 12,
    css: css`
        padding: 0 8px;
        border-left: 1px solid ${color("common.border")};
    `,
};

newsListViewStyles.listCategoryLink = {
    typographyProps: {
        size: 12,
        css: css`
            margin-left: 5px;
            text-transform: capitalize;
        `,
    },
};

newsListViewStyles.loadMore = {
    variant: "tertiary",
    size: 32,
    typographyProps: {
        size: 14,
    },
    css: css`
        position: relative;
        width: 100%;
        border-color: ${color("common.border")};
    `,
};

newsListViewStyles.loadMoreIcon = {
    color: "primary",
    css: css`
        svg {
            height: 21px;
            width: 19px;
        }
        position: relative;
        top: 6px;
        left: 3px;
        opacity: 0.33;
        justify-content: end;
    `,
};

newsListViewStyles.container = {
    gap: 22,
    css: css`
        margin-top: 52px;
    `,
};

newsListViewStyles.zoneAndListGridItem = {
    width: [12, 12, 12, 9, 9],
    css: css`
        flex-direction: column;
    `,
};

newsListViewStyles.zoneWrapper = {
    css: css`
        width: 100%;
    `,
};

newsListViewStyles.listWrapper = {
    css: css`
        width: 100%;
    `,
};

newsListViewStyles.categoryGridItem = {
    width: 3,
};

newsListViewStyles.categoryHidden = {
    css: css`
        width: 100%;
    `,
};

newsListViewStyles.categoryWrapper = {
    css: css`
        width: 100%;
        border: 1px solid ${color("common.border")};
        border-radius: 4px;
        margin-bottom: 22px;
    `,
};

newsListViewStyles.categoryTitle = {
    variant: "h3",
    css: css`
        margin: 16px 16px 0;
        padding-bottom: 12px;
        border-bottom: 1px solid ${color("common.border")};
    `,
};

newsListViewStyles.categoryLinksFancyScroll = {
    css: css`
        padding: 4px 9px 9px;
    `,
};

newsListViewStyles.categoryLinksContainer = {
    css: css`
        max-height: 315px;
        ${styledScrollbar(4)}
        ${xScreen("lte", "md")} {
            max-height: 145px;
        }
    `,
};

newsListViewStyles.categoryLinkWrapper = {
    css: css`
        margin: 4px 0;
        padding: 3px 5px 3px 8px;
        cursor: pointer;
        transition: background-color 200ms ease-in-out;
        text-transform: capitalize;

        &:hover {
            background-color: ${color("common.ui2")};
            box-shadow: inset 2px 0 0 0 ${color("common.ui9")};
        }
    `,
};

newsListViewStyles.selectedCategoryLinkWrapper = {
    css: css`
        margin: 4px 0;
        padding: 3px 5px 3px 8px;
        cursor: pointer;
        text-transform: capitalize;

        background-color: ${color("common.ui2")};
        box-shadow: inset 2px 0 0 0 ${color("common.ui9")};
        border-radius: 2px;
    `,
};

newsListViewStyles.categoryLink = {
    color: "secondary",
    typographyProps: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
        `,
    },
    css: css`
        width: 100%;
    `,
};

newsListViewStyles.selectedCategoryLink = {
    color: "common.ui9",
    typographyProps: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
        `,
    },
    css: css`
        width: 100%;
    `,
};
newsListViewStyles.categoryCount = {
    size: 14,
    lineHeight: "16px",
    weight: 400,
};
